import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import IntranetLayout from "@/components/IntranetLayout";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Upload, Send, Paperclip, Plus, ArrowLeft, Link2, CheckCircle2, XCircle,
  GitBranch, Download, Trash2, Clock,
} from "lucide-react";
import { toast } from "sonner";

type Project = any;
type Task = any;
type Message = any;
type Doc = any;
type Approval = any;
type Assignee = any;

const stageOptions = ["startup", "in_progress", "awaiting_approval", "approved", "completed", "rejected"];
const stageLabel = (s: string) => s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function EarMarDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [docs, setDocs] = useState<Doc[]>([]);
  const [approvals, setApprovals] = useState<Approval[]>([]);
  const [assignees, setAssignees] = useState<Assignee[]>([]);
  const [linkedRefs, setLinkedRefs] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [draftMsg, setDraftMsg] = useState("");
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const deliverableInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    if (!id) return;
    const [{ data: p }, { data: t }, { data: m }, { data: d }, { data: a }, { data: ass }] = await Promise.all([
      supabase.from("ear_mar_projects").select("*").eq("id", id).single(),
      supabase.from("ear_mar_tasks").select("*").eq("project_id", id).order("created_at"),
      supabase.from("ear_mar_messages").select("*").eq("project_id", id).order("created_at"),
      supabase.from("ear_mar_documents").select("*").eq("project_id", id).order("uploaded_at", { ascending: false }),
      supabase.from("ear_mar_approvals").select("*").eq("project_id", id).order("step_order"),
      supabase.from("ear_mar_assignees").select("*").eq("project_id", id),
    ]);
    setProject(p); setTasks(t || []); setMessages(m || []); setDocs(d || []); setApprovals(a || []); setAssignees(ass || []);
    if (p?.linked_project_ids?.length) {
      const { data: refs } = await supabase.from("ear_mar_projects").select("id, project_number, title, type, stage").in("id", p.linked_project_ids);
      setLinkedRefs(refs || []);
    } else setLinkedRefs([]);
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Realtime chat + tasks
  useEffect(() => {
    if (!id) return;
    const ch = supabase
      .channel(`ear-mar-${id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "ear_mar_messages", filter: `project_id=eq.${id}` }, () => {
        supabase.from("ear_mar_messages").select("*").eq("project_id", id).order("created_at").then(({ data }) => setMessages(data || []));
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "ear_mar_tasks", filter: `project_id=eq.${id}` }, () => {
        supabase.from("ear_mar_tasks").select("*").eq("project_id", id).order("created_at").then(({ data }) => setTasks(data || []));
      })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [id]);

  const sendMessage = async () => {
    if (!draftMsg.trim() || !user || !id) return;
    const body = draftMsg.trim();
    setDraftMsg("");
    const { error } = await supabase.from("ear_mar_messages").insert({ project_id: id, user_id: user.id, body });
    if (error) toast.error(error.message);
  };

  const addTask = async () => {
    if (!newTaskTitle.trim() || !user || !id) return;
    const { error } = await supabase.from("ear_mar_tasks").insert({ project_id: id, title: newTaskTitle.trim(), created_by: user.id });
    if (error) toast.error(error.message); else setNewTaskTitle("");
  };

  const setTaskStatus = async (taskId: string, status: string) => {
    const patch: any = { status };
    if (status === "done") patch.completed_at = new Date().toISOString();
    await supabase.from("ear_mar_tasks").update(patch).eq("id", taskId);
  };

  const uploadFile = async (file: File, kind: "reference" | "deliverable") => {
    if (!user || !id) return;
    const path = `${id}/${kind}/${Date.now()}-${file.name}`;
    const { error: upErr } = await supabase.storage.from("ear-mar-files").upload(path, file);
    if (upErr) { toast.error(upErr.message); return; }
    const { error } = await supabase.from("ear_mar_documents").insert({
      project_id: id, kind, file_path: path, file_name: file.name, file_size: file.size, uploaded_by: user.id,
    });
    if (error) toast.error(error.message); else { toast.success("Uploaded"); load(); }
  };

  const downloadDoc = async (doc: Doc) => {
    const { data, error } = await supabase.storage.from("ear-mar-files").createSignedUrl(doc.file_path, 60);
    if (error || !data) { toast.error("Could not generate link"); return; }
    window.open(data.signedUrl, "_blank");
  };

  const toggleDeliverable = async (doc: Doc) => {
    await supabase.from("ear_mar_documents").update({
      accepted: !doc.accepted,
      accepted_by: !doc.accepted ? user?.id : null,
      accepted_at: !doc.accepted ? new Date().toISOString() : null,
    }).eq("id", doc.id);
    load();
  };

  const decideApproval = async (approval: Approval, decision: "approved" | "rejected") => {
    if (approval.approver_user_id !== user?.id) { toast.error("You are not the assigned approver"); return; }
    await supabase.from("ear_mar_approvals").update({
      decision, decided_at: new Date().toISOString(),
    }).eq("id", approval.id);

    // After decision, advance project stage
    const updated = approvals.map(a => a.id === approval.id ? { ...a, decision } : a);
    if (decision === "rejected") {
      await supabase.from("ear_mar_projects").update({ stage: "rejected" }).eq("id", id);
    } else {
      const allApproved = updated.every(a => a.decision === "approved");
      if (allApproved) {
        await supabase.from("ear_mar_projects").update({ stage: "approved" }).eq("id", id);
      } else {
        await supabase.from("ear_mar_projects").update({ stage: "awaiting_approval" }).eq("id", id);
      }
    }
    load();
  };

  const changeStage = async (stage: string) => {
    if (stage === "completed") {
      const deliverables = docs.filter(d => d.kind === "deliverable");
      if (deliverables.length === 0 || deliverables.some(d => !d.accepted)) {
        toast.error("All deliverables must be uploaded and accepted before completion.");
        return;
      }
    }
    await supabase.from("ear_mar_projects").update({
      stage: stage as any, completed_at: stage === "completed" ? new Date().toISOString() : null,
    }).eq("id", id);
    load();
  };

  const spawnMoc = async () => {
    if (!user || !project) return;
    const { data, error } = await supabase.from("ear_mar_projects").insert({
      type: "MOC",
      title: `MOC for ${project.project_number} — ${project.title}`,
      description: `Linked precursor MOC for ${project.project_number}.`,
      vessel_name: project.vessel_name,
      estimated_cost_usd: 0,
      linked_project_ids: [project.id],
      created_by: user.id,
    }).select("id").single();
    if (error || !data) { toast.error(error?.message || "Failed"); return; }
    await supabase.from("ear_mar_assignees").insert({ project_id: data.id, user_id: user.id, role: "owner" });
    toast.success("MOC created");
    navigate(`/employee/ear-mar/${data.id}`);
  };

  const linkExistingProject = async (refId: string) => {
    if (!project) return;
    const next = Array.from(new Set([...(project.linked_project_ids || []), refId]));
    await supabase.from("ear_mar_projects").update({ linked_project_ids: next }).eq("id", project.id);
    load();
  };
  const unlinkProject = async (refId: string) => {
    if (!project) return;
    const next = (project.linked_project_ids || []).filter((x: string) => x !== refId);
    await supabase.from("ear_mar_projects").update({ linked_project_ids: next }).eq("id", project.id);
    load();
  };

  const deliverables = useMemo(() => docs.filter(d => d.kind === "deliverable"), [docs]);
  const references = useMemo(() => docs.filter(d => d.kind === "reference"), [docs]);

  if (loading || !project) return <IntranetLayout><p className="p-6 text-sm text-muted-foreground">Loading…</p></IntranetLayout>;

  return (
    <IntranetLayout>
      <Link to="/employee/ear-mar" className="inline-flex items-center text-xs text-muted-foreground hover:text-foreground mb-3"><ArrowLeft className="w-3 h-3 mr-1" /> Back to list</Link>

      {/* Header */}
      <div className="mb-4">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className="text-[11px] font-bold tracking-wider text-muted-foreground">{project.project_number}</span>
          <Badge variant="outline">{project.type}</Badge>
          <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30">{stageLabel(project.stage)}</Badge>
          {project.estimated_cost_usd > 50000 && <Badge className="bg-purple-500/15 text-purple-300">COO route</Badge>}
        </div>
        <h1 className="text-2xl font-bold">{project.title}</h1>
        <p className="text-sm text-muted-foreground">{project.vessel_name || "No vessel"} · Est. ${Number(project.estimated_cost_usd).toLocaleString("en-US")}</p>
      </div>

      {/* Quick reference bar */}
      {linkedRefs.length > 0 && (
        <Card className="mb-4 border-teal/30">
          <CardContent className="p-3">
            <p className="text-[10px] uppercase tracking-wider text-teal mb-2 flex items-center gap-1"><Link2 className="w-3 h-3" /> Quick Reference Projects</p>
            <div className="flex flex-wrap gap-2">
              {linkedRefs.map(r => (
                <Link key={r.id} to={`/employee/ear-mar/${r.id}`} className="text-xs px-2 py-1 rounded-md bg-teal/10 text-teal hover:bg-teal/20">
                  {r.project_number} · {r.title}
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="documents">Documents ({references.length})</TabsTrigger>
          <TabsTrigger value="tasks">Tasks ({tasks.length})</TabsTrigger>
          <TabsTrigger value="discussion">Discussion ({messages.length})</TabsTrigger>
          <TabsTrigger value="deliverables">Deliverables ({deliverables.length})</TabsTrigger>
          <TabsTrigger value="approvals">Approvals</TabsTrigger>
          <TabsTrigger value="links">Linked Refs</TabsTrigger>
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview" className="mt-4 space-y-4">
          <Card><CardContent className="p-4">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Description</p>
            <p className="text-sm whitespace-pre-wrap">{project.description || <span className="italic text-muted-foreground">No description.</span>}</p>
          </CardContent></Card>

          <div className="grid sm:grid-cols-2 gap-3">
            <Card><CardContent className="p-4 space-y-2">
              <Label>Stage</Label>
              <Select value={project.stage} onValueChange={changeStage}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {stageOptions.map(s => <SelectItem key={s} value={s}>{stageLabel(s)}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">Stage auto-advances as approvals are decided. "Completed" requires every deliverable to be marked accepted.</p>
            </CardContent></Card>

            <Card><CardContent className="p-4 space-y-2">
              <Label>Spawn linked MOC</Label>
              <p className="text-[11px] text-muted-foreground">Create a Management-of-Change record linked to this project. Useful as a precursor to an EAR/MAR.</p>
              <Button size="sm" variant="outline" onClick={spawnMoc}><GitBranch className="w-3 h-3 mr-1" /> Create linked MOC</Button>
            </CardContent></Card>
          </div>

          <Card><CardContent className="p-4">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Team ({assignees.length})</p>
            <div className="flex flex-wrap gap-2">
              {assignees.map(a => (
                <Badge key={a.id} variant="outline" className="text-xs">{a.role}: {a.user_id.slice(0, 8)}</Badge>
              ))}
            </div>
          </CardContent></Card>
        </TabsContent>

        {/* DOCUMENTS */}
        <TabsContent value="documents" className="mt-4 space-y-3">
          <Card><CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="font-semibold">Reference documents</p>
                <p className="text-[11px] text-muted-foreground">Drawings, specifications, photos, dispensation forms.</p>
              </div>
              <Button size="sm" onClick={() => fileInputRef.current?.click()}><Upload className="w-3 h-3 mr-1" /> Upload</Button>
              <input ref={fileInputRef} type="file" hidden onChange={e => e.target.files?.[0] && uploadFile(e.target.files[0], "reference")} />
            </div>
            {references.length === 0 ? <p className="text-sm text-muted-foreground">No documents yet.</p> : (
              <div className="space-y-1">
                {references.map(d => (
                  <div key={d.id} className="flex items-center gap-2 p-2 rounded-md hover:bg-muted/50">
                    <Paperclip className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm flex-1 truncate">{d.file_name}</span>
                    <span className="text-[10px] text-muted-foreground">{new Date(d.uploaded_at).toLocaleDateString("en-US")}</span>
                    <Button size="sm" variant="ghost" onClick={() => downloadDoc(d)}><Download className="w-3 h-3" /></Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent></Card>
        </TabsContent>

        {/* TASKS */}
        <TabsContent value="tasks" className="mt-4 space-y-3">
          <Card><CardContent className="p-4 space-y-3">
            <div className="flex gap-2">
              <Input placeholder="New task title…" value={newTaskTitle} onChange={e => setNewTaskTitle(e.target.value)} onKeyDown={e => e.key === "Enter" && addTask()} />
              <Button onClick={addTask}><Plus className="w-3 h-3 mr-1" /> Add</Button>
            </div>
            {tasks.length === 0 ? <p className="text-sm text-muted-foreground">No tasks.</p> : (
              <div className="space-y-1">
                {tasks.map(t => (
                  <div key={t.id} className="flex items-center gap-2 p-2 rounded-md hover:bg-muted/50">
                    <Checkbox checked={t.status === "done"} onCheckedChange={() => setTaskStatus(t.id, t.status === "done" ? "open" : "done")} />
                    <span className={`text-sm flex-1 ${t.status === "done" ? "line-through text-muted-foreground" : ""}`}>{t.title}</span>
                    <Select value={t.status} onValueChange={(v) => setTaskStatus(t.id, v)}>
                      <SelectTrigger className="h-7 w-32 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="in_progress">In progress</SelectItem>
                        <SelectItem value="blocked">Blocked</SelectItem>
                        <SelectItem value="done">Done</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            )}
          </CardContent></Card>
        </TabsContent>

        {/* DISCUSSION */}
        <TabsContent value="discussion" className="mt-4">
          <Card><CardContent className="p-0">
            <div className="h-[420px] overflow-y-auto p-4 space-y-2">
              {messages.length === 0 ? <p className="text-sm text-muted-foreground">No messages yet. Start the conversation.</p> :
                messages.map(m => {
                  const mine = m.user_id === user?.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[70%] rounded-lg px-3 py-2 text-sm ${mine ? "bg-teal/20 text-foreground" : "bg-muted"}`}>
                        <p className="whitespace-pre-wrap">{m.body}</p>
                        <p className="text-[10px] opacity-60 mt-1 flex items-center gap-1"><Clock className="w-2.5 h-2.5" /> {new Date(m.created_at).toLocaleString("en-US")}</p>
                      </div>
                    </div>
                  );
                })}
            </div>
            <div className="border-t p-3 flex gap-2">
              <Input placeholder="Type a message…" value={draftMsg} onChange={e => setDraftMsg(e.target.value)} onKeyDown={e => e.key === "Enter" && sendMessage()} />
              <Button onClick={sendMessage}><Send className="w-3 h-3" /></Button>
            </div>
          </CardContent></Card>
        </TabsContent>

        {/* DELIVERABLES */}
        <TabsContent value="deliverables" className="mt-4 space-y-3">
          <Card><CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="font-semibold">Final deliverables</p>
                <p className="text-[11px] text-muted-foreground">Upload final outputs and tick to mark accepted. All must be accepted to mark the project Completed.</p>
              </div>
              <Button size="sm" onClick={() => deliverableInputRef.current?.click()}><Upload className="w-3 h-3 mr-1" /> Upload deliverable</Button>
              <input ref={deliverableInputRef} type="file" hidden onChange={e => e.target.files?.[0] && uploadFile(e.target.files[0], "deliverable")} />
            </div>
            {deliverables.length === 0 ? <p className="text-sm text-muted-foreground">No deliverables yet.</p> : (
              <div className="space-y-1">
                {deliverables.map(d => (
                  <div key={d.id} className="flex items-center gap-2 p-2 rounded-md hover:bg-muted/50">
                    <Checkbox checked={d.accepted} onCheckedChange={() => toggleDeliverable(d)} />
                    <Paperclip className="w-4 h-4 text-muted-foreground" />
                    <span className={`text-sm flex-1 truncate ${d.accepted ? "line-through text-muted-foreground" : ""}`}>{d.file_name}</span>
                    {d.accepted && <Badge className="bg-emerald-500/15 text-emerald-300">Accepted</Badge>}
                    <Button size="sm" variant="ghost" onClick={() => downloadDoc(d)}><Download className="w-3 h-3" /></Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent></Card>
        </TabsContent>

        {/* APPROVALS */}
        <TabsContent value="approvals" className="mt-4 space-y-3">
          {approvals.length === 0 ? (
            <Card><CardContent className="p-4 text-sm text-muted-foreground">
              No approval chain set up. The admin needs to configure approvers (Dept Head, Technical Director, COO).
            </CardContent></Card>
          ) : approvals.map(a => {
            const mine = a.approver_user_id === user?.id && a.decision === "pending";
            return (
              <Card key={a.id}><CardContent className="p-4 flex items-center gap-3">
                <Badge variant="outline" className="font-mono">{a.step_order}</Badge>
                <div className="flex-1">
                  <p className="text-sm font-semibold">{stageLabel(a.role)}</p>
                  <p className="text-[11px] text-muted-foreground">
                    Approver: {a.approver_user_id ? a.approver_user_id.slice(0, 8) + "…" : <span className="italic">unassigned</span>}
                  </p>
                </div>
                <Badge className={
                  a.decision === "approved" ? "bg-emerald-500/15 text-emerald-300" :
                  a.decision === "rejected" ? "bg-red-500/15 text-red-300" :
                  "bg-amber-500/15 text-amber-300"
                }>{a.decision}</Badge>
                {mine && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => decideApproval(a, "rejected")}><XCircle className="w-3 h-3 mr-1" /> Reject</Button>
                    <Button size="sm" onClick={() => decideApproval(a, "approved")} className="bg-emerald-600 hover:bg-emerald-600/90"><CheckCircle2 className="w-3 h-3 mr-1" /> Approve</Button>
                  </>
                )}
              </CardContent></Card>
            );
          })}
        </TabsContent>

        {/* LINKED REFS */}
        <TabsContent value="links" className="mt-4 space-y-3">
          <Card><CardContent className="p-4 space-y-3">
            <p className="text-[11px] text-muted-foreground">Pin similar past or parallel projects for one-click context at the top of this page.</p>
            <LinkedPicker excludeId={project.id} alreadyLinked={project.linked_project_ids || []} onPick={linkExistingProject} />
            <div className="space-y-1">
              {linkedRefs.map(r => (
                <div key={r.id} className="flex items-center gap-2 p-2 rounded-md hover:bg-muted/50">
                  <Link2 className="w-3 h-3 text-teal" />
                  <Link to={`/employee/ear-mar/${r.id}`} className="text-sm flex-1 hover:underline">{r.project_number} · {r.title}</Link>
                  <Badge variant="outline" className="text-[10px]">{r.type}</Badge>
                  <Button size="sm" variant="ghost" onClick={() => unlinkProject(r.id)}><Trash2 className="w-3 h-3" /></Button>
                </div>
              ))}
            </div>
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </IntranetLayout>
  );
}

function LinkedPicker({ excludeId, alreadyLinked, onPick }: { excludeId: string; alreadyLinked: string[]; onPick: (id: string) => void }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<any[]>([]);
  useEffect(() => {
    if (!q) { setResults([]); return; }
    const t = setTimeout(async () => {
      const { data } = await supabase.from("ear_mar_projects")
        .select("id, project_number, title, type")
        .or(`title.ilike.%${q}%,project_number.ilike.%${q}%`).limit(8);
      setResults((data || []).filter(r => r.id !== excludeId && !alreadyLinked.includes(r.id)));
    }, 250);
    return () => clearTimeout(t);
  }, [q, excludeId, alreadyLinked]);
  return (
    <div className="space-y-2">
      <Input placeholder="Search projects to link…" value={q} onChange={e => setQ(e.target.value)} />
      {results.length > 0 && (
        <div className="border rounded-md divide-y">
          {results.map(r => (
            <button key={r.id} onClick={() => { onPick(r.id); setQ(""); }} className="w-full text-left p-2 hover:bg-muted text-sm flex items-center gap-2">
              <Badge variant="outline" className="text-[10px]">{r.type}</Badge>
              <span className="text-muted-foreground text-xs">{r.project_number}</span>
              <span className="flex-1 truncate">{r.title}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
